/**
 * Good test inputs:
 *  63110, 10-miles (Standard view, has 6 top themes)
 *  63110, 1-mile (Has 3 top themes, one less than what's in the design)
 *  86301, 1-mile (No top themes)
 *  99501, any radius (Not a ZIP code that is part of the continguous US. Not valid for Radian.)
 */

import React from 'react';
import ReactPDF, {
  Document,
  Font,
  Link,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';

import _ from 'lodash';

import getLogoDimensionsForView from '../../utils/getLogoDimensionsForView';
import uploadPdfBlobWithOpts from '../../../../_libs/utils/uploadPdfBlobWithOptions';
import PdfImageFromAzureWithOptions from '../../../../atoms/PdfImageFromAzureWithOptions';
import PdfImageFromAzureWithUri from '../../../../atoms/PdfImageFromAzureWithUri';
import PdfCropView from '../../../../organisms/PdfCropView';

const PDF_BLEED = 4.5;
const PDF_WIDTH = 792;
const PDF_HEIGHT = 612;

// Do not hypenate words in this document
Font.registerHyphenationCallback(word => [word]);

function addBleed (value: number, formatForPrint: boolean, bleed = PDF_BLEED) {
  return (
    formatForPrint
      ? value + bleed
      : value
  );
}

const ReportCardTemplatePageOne = ({
  formatForPrint = false,
}): JSX.Element => {
  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    >
      <PdfImageFromAzureWithOptions
        fileName={`1_template_bg${formatForPrint ? '_full_bleed' : ''}.png`}
        folderPath="assets/pdf_graphics/"
        style={{
          width: addBleed(PDF_WIDTH, formatForPrint, PDF_BLEED * 2),
          height: addBleed(PDF_HEIGHT, formatForPrint, PDF_BLEED * 2),
        }}
      />
    </View>
  );
};

const ReportCardTemplatePageTwo = ({
  formatForPrint = false,
}): JSX.Element => {
  return (
    <Page
      size={[
        addBleed(PDF_HEIGHT, formatForPrint, PDF_BLEED * 2),
        addBleed(PDF_WIDTH, formatForPrint, PDF_BLEED * 2),
      ]}
      orientation="landscape"
    >
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <PdfImageFromAzureWithOptions
          fileName={`2_template_page${formatForPrint ? '_full_bleed' : ''}.png`}
          folderPath="assets/pdf_graphics/"
          style={{
            width: addBleed(PDF_WIDTH, formatForPrint, PDF_BLEED * 2),
            height: addBleed(PDF_HEIGHT, formatForPrint, PDF_BLEED * 2),
          }}
        />
      </View>
      {
        formatForPrint
          && <PdfCropView
                width={addBleed(PDF_WIDTH, formatForPrint, PDF_BLEED * 2)}
                height={addBleed(PDF_HEIGHT, formatForPrint, PDF_BLEED * 2)}
                markLength={PDF_BLEED}
              />
      }
    </Page>
  );
};

const ReportCard = ({
  data,
  printTemplate = false,
  formatForPrint = false,
}): JSX.Element => {
  const brandColor = (data.primaryBrand) ? data.primaryBrand.colors.primary : ((data.color) ? data.color : 'rgb(0,0,0)');
  const logoHeight = (data.primaryBrand) ? data.primaryBrand.logoHeight : data.logo_height;
  const logoWidth = (data.primaryBrand) ? data.primaryBrand.logoWidth : data.logo_width;
  const logoUrl = (data.primaryBrand) ? data.primaryBrand.logoUrl : data.logo_url;
  const organizationName = (data.primaryBrand) ? data.primaryBrand.name : data.name;
  const reportData: AggregateDataType = data.reportData;
  const NPIs = data.reportData.NPIs;
  const NPIsMax = data.reportData.NPIsMax;

  const Styles = StyleSheet.create({
    Page: {
      padding: addBleed(36, formatForPrint),
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      fontSize: 9,
      lineHeight: 12/9,
      fontFamily: 'Helvetica',
    },
    Column: {
      height: 540,
      width: 192,
      flexDirection: 'column',
    },
    ColumnHeader: {
      height: 54,
      marginBottom: 6,
      display: 'flex',
      justifyContent: 'center',
    },
    ColumnBody: {
      height: 420,
      marginBottom: 6,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    ColumnFooter: {
      height: 70,
    },
    LogoView: {
      width: 192,
      height: 32,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    StatBubble: {
      marginTop: 16,
      height: 73,
      padding: 12,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
  
    },
    CopyEmphasis: {
      fontFamily: 'Helvetica-Bold',
    },
    Link: {
      color: brandColor,
    },
    StatLarge: {
      fontSize: 32,
      lineHeight: 1,
      color: brandColor,
      fontFamily: 'Helvetica-Bold',
    },
    Stat: {
      fontSize: 24,
      lineHeight: 1,
      color: brandColor,
      fontFamily: 'Helvetica-Bold',
      marginBottom: 2,
    },
    SubheaderView: {
      marginBottom: 4,
      display: 'flex',
      flexDirection: 'row',
      alignContent: 'center',
    },
    Subheader: {
      fontFamily: 'Helvetica-Bold',
      fontSize: 9,
      lineHeight: 0.75,
      textTransform: 'uppercase',
    },
    SubheaderIcon: {
      width: 12,
      height: 12,
      marginRight: 2,
      marginTop: -1,
    },
    Title: {
      fontFamily: 'Times-Italic',
      fontSize: 16,
      lineHeight: 1,
    },
  });
  const logoDimensions = getLogoDimensionsForView({
    width: logoWidth,
    height: logoHeight,
  }, {
    width: Styles.LogoView.width,
    height: Styles.LogoView.height
  });
  const Emphasis = ({
    children,
    color = null,
  }): JSX.Element => {
    return (
      <Text style={{
        ...Styles.CopyEmphasis,
        color: color ? color : brandColor,
      }}>
        {children}
      </Text>
    );
  };
  
  // TODO: Replace with components in bulkUpload branch
  const OneLiner = ({
    label,
    value,
  }): JSX.Element => {
    return (
      <View>
        <Text>
          <Text style={ Styles.CopyEmphasis }>{label}</Text>
          <Text> · {value}</Text>
        </Text>
      </View>
    );
  };
  const DataGraph = ({
    label,
    value,
    percent,
    color = brandColor,
  }):JSX.Element => {
    return (
      <View style={{
        width: '100%',
        marginBottom: 6,
      }}>
        <OneLiner label={label} value={value} />
        <View
          style={{
            position: 'relative',
            height: 3,
            marginTop: 2,
          }}
        >
          <View
            style={{
              position: 'absolute',
              width: `${isNaN(percent) ? 0 : percent}%`,
              backgroundColor: color,
              top: 0,
              left: 0,
              height: 3,
            }}
          />
          <View
            style={{
              position: 'absolute',
              width: '100%',
              backgroundColor: color,
              opacity: 0.25,
              top: 0,
              left: 0,
              height: 3,
            }}
          />
        </View>
      </View>
    );
  };
  const DataBlurb = ({
    header,
    statistic,
    description = null,
    level = null,
  }): JSX.Element => {
    return (
      <View style={{
        marginBottom: 9,
      }}>
        <Text>{level}</Text>
        <Text style={Styles.CopyEmphasis}>{header}</Text>
        <Text style={Styles.Stat}>{statistic}</Text>
        <Text wrap>{description}</Text>
      </View>
    );
  };
  const DataAIOpportunity = ({
    icon,
    theme,
    prevalenceCount,
    count,
  }): JSX.Element => {
    return (
      <View style={Styles.StatBubble}>
        <View>
          <View style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 2,
          }}>
            <PdfImageFromAzureWithOptions
              fileName={`icon_${icon}.png`}
              folderPath="assets/icons/"
              style={{
                width: 24,
                height: 24,
                marginTop: 3,
              }}
            />
            <Text style={{
              ...Styles.Stat,
              marginLeft: 4,
            }}
            >{(prevalenceCount/count*100).toFixed(2)}%</Text>
          </View>
          <Text style={Styles.CopyEmphasis}>{theme}</Text>
          <Text>
            <Emphasis>{prevalenceCount}</Emphasis> of {count} Census block groups
          </Text>
        </View>
      </View>
    );
  };

  function getWalkabilityLabel (value: number) {
    if (value <= 5.75) {
      return 'Least Walkable';
    } else if (value <= 10.5) {
      return 'Below Average Walkable';
    } else if (value <= 15.25) {
      return 'Above Average Walkable';
    } else {
      return 'Most Walkable';
    }
  }

  const LbEmail = (): JSX.Element => (
    <Link
      src="mailto:info@lightbeamhealth.com" 
      style={Styles.Link}>
      <Text>info@lightbeamhealth.com</Text>
    </Link>
    );

  /**
   * This summary prints on top of a pre-printed paper. The paper has 5 specific slots upon which content is printed.
   * If there are fewer than four insights printed, show the TooFewThemesCta.
  */
  function formatTopThemes (topThemes: any[], themeData: any, blockCount: number) {
    const NoOpportunityIdentifiedCta = ():JSX.Element => (
      <>
        <View style={Styles.StatBubble}>
          <PdfImageFromAzureWithOptions
            fileName="icon_ban.png"
            folderPath="assets/icons/"
            style={Styles.SubheaderIcon}
          />
          <Text>
            <Emphasis color={'black'}>Zero opportunities were surfaced for this area.</Emphasis>
          </Text>
        </View>
        <View style={Styles.StatBubble}>
        </View>
        <View style={Styles.StatBubble}>
          <Text>
            Most areas have four or more opportunities.
          </Text>
        </View>
        <View style={Styles.StatBubble}>
          <Text>
            Ask a Lightbeam representative to generate a summary for a new area.
          </Text>
        </View>
        <View style={Styles.StatBubble}>
          <Text>
            Or, email <LbEmail/>.
          </Text>
        </View>
      </>
    );
    // No opportunities, or themes, surfaced at all
    if (topThemes.length === 0) {
      return <NoOpportunityIdentifiedCta />;
    // False positive of one theme surfaced
    } else if (topThemes.length === 1 && topThemes[0].prevalenceCount === 0) {
      return <NoOpportunityIdentifiedCta />;
    } else {
      const blocks = [];
      const PrimaryCta = (): JSX.Element => {
        return (
          <View style={Styles.StatBubble}>
            <Text wrap>
              Your Radian report includes <Emphasis>{parseInt(data.clinicalGuideStats.recommendations).toLocaleString('en-US')}</Emphasis> <Emphasis color={'black'}>evidence-based recommendations</Emphasis> to address the opportunity above. Email <LbEmail/> to get it.
            </Text>
          </View>
        );
      };
      const TooFewThemesCta = (): JSX.Element => {
        return (
          <View style={Styles.StatBubble}>
            <Text>
              <Emphasis color={'black'}>Did you know?</Emphasis> Radian usually generates 4 or more opportunities for a given area. Sign up for a demo to explore more locations or to broaden its search radius.</Text>
          </View>
        );
      };
      _.each(topThemes, (theme, i) => {
        if (i > 3) {
          return;
        }
        const data = themeData[theme.themeId];
        blocks.push(
          <DataAIOpportunity
            icon={data.icons.split(',')[0]}
            theme={data.name}
            prevalenceCount={theme.prevalenceCount}
            count={blockCount}
            key={blocks.length}
          />
        );
      });
      blocks.push(<PrimaryCta key={blocks.length} />);
      if (blocks.length < 5) {
        blocks.push(<TooFewThemesCta key={blocks.length} />);
      }
      return blocks;
    }
  }

  // NOTE: The same PDF may be uploaded multiple times if this component is re-rendered, consider a debouncer
  function handlePdfRender(props: ReactPDF.OnRenderProps) {
    if (!props.blob) {
      console.warn('PDF appears to have stopped rendering but does not have blob data.');
      return;
    }
    // Don't worry about this response–it is not crucial to the end user.
    // If it fails, that's OK. There is an event logged on the ReportCardPage
    // to log the data required to create this asset
    uploadPdfBlobWithOpts(
      props.blob,
      {
        organizationName,
        radius: data.radius,
        zip: data.zip,
        container: data.graphUserId,
        folderPath: 'pdfs/report_cards/',
      },
    );
  }

  return (
    <Document
      onRender={handlePdfRender}
    >
      <Page
        size={[
          addBleed(PDF_HEIGHT, formatForPrint, PDF_BLEED * 2),
          addBleed(PDF_WIDTH, formatForPrint, PDF_BLEED * 2),
        ]}
        orientation="landscape"
      >
        {
          printTemplate
            && <ReportCardTemplatePageOne formatForPrint={formatForPrint} />
        }
        <View style={Styles.Page}>
          <View style={Styles.Column}>
            <View style={Styles.ColumnHeader}>
              <View style={Styles.LogoView}>
                {
                  logoUrl
                    ? <PdfImageFromAzureWithUri
                        fileUri={logoUrl}
                        style={{
                          width: logoDimensions.width,
                          height:logoDimensions.height,
                        }}
                      />
                    : <Text style={Styles.Title}>{organizationName}</Text>
                }
                
              </View>
            </View>
            <View style={Styles.ColumnBody}>
              <View>
                <Text>
                  Your Radian summary includes <Emphasis>{reportData.count.toLocaleString('en-US')}</Emphasis> Census block {reportData.count === 1 ? 'group' : 'groups'} near <Emphasis>{data.zipData.placeName}</Emphasis>. The block groups referenced are located within <Emphasis>{data.radius} {parseInt(data.radius) === 1 ? 'mile' : 'miles'}</Emphasis> of the center of the ZIP code <Emphasis>{data.zipData.zipCode}</Emphasis>.
                </Text>
              </View>
              <View>
                <View style={Styles.SubheaderView}>
                  <PdfImageFromAzureWithOptions
                    fileName="icon_users.png"
                    folderPath="assets/icons/"
                    style={Styles.SubheaderIcon}
                  />
                  <Text style={Styles.Subheader}>
                    Estimated Population
                  </Text>
                </View>
                <Text style={Styles.StatLarge}>{reportData.household_est.toLocaleString('en-US')}</Text>
              </View>
              <View>
                <View style={Styles.SubheaderView}>
                  <PdfImageFromAzureWithOptions
                    fileName="icon_street-view.png"
                    folderPath="assets/icons/"
                    style={Styles.SubheaderIcon}
                  />
                  <Text style={Styles.Subheader}>
                    Reported Race / Ethnicity
                  </Text>
                </View>
                <DataGraph
                  label="Asian / Pacific Islander"
                  value={`${(reportData.race_asian_pi*100).toFixed(2)}%`}
                  percent={(reportData.race_asian_pi*100).toFixed(2)}
                />
                <DataGraph
                  label="Black"
                  value={`${(reportData.race_black*100).toFixed(2)}%`}
                  percent={(reportData.race_black*100).toFixed(2)}
                />
                <DataGraph
                  label="Native American"
                  value={`${(reportData.race_native_amer*100).toFixed(2)}%`}
                  percent={(reportData.race_native_amer*100).toFixed(2)}
                />
                <DataGraph
                  label="Other"
                  value={`${(reportData.race_other*100).toFixed(2)}%`}
                  percent={(reportData.race_other*100).toFixed(2)}
                />
                <DataGraph
                  label="White"
                  value={`${(reportData.race_white*100).toFixed(2)}%`}
                  percent={(reportData.race_white*100).toFixed(2)}
                />
                <DataGraph
                  label="Hispanic"
                  value={`${(reportData.ethnicity_hispanic*100).toFixed(2)}%`}
                  percent={(reportData.ethnicity_hispanic*100).toFixed(2)}
                />
              </View>
              <View>
                <View style={Styles.SubheaderView}>
                  <PdfImageFromAzureWithOptions
                    fileName="icon_calendar-lines.png"
                    folderPath="assets/icons/"
                    style={Styles.SubheaderIcon}
                  />
                  <Text style={Styles.Subheader}>
                    Reported Age
                  </Text>
                </View>
                <DataGraph
                  label="17 and Under"
                  value={`${(reportData.ep_age17_value).toFixed(2)}%`}
                  percent={(reportData.ep_age17_value).toFixed(2)}
                />
                <DataGraph
                  label="18-64"
                  value={`${(100-reportData.ep_age17_value-reportData.ep_age65_value).toFixed(2)}%`}
                  percent={(100-reportData.ep_age17_value-reportData.ep_age65_value).toFixed(2)}
                />
                <DataGraph
                  label="65 and Over"
                  value={`${(reportData.ep_age65_value).toFixed(2)}%`}
                  percent={(reportData.ep_age65_value).toFixed(2)}
                />
              </View>
            </View>
            <View style={Styles.ColumnFooter} />
          </View>
          <View style={Styles.Column}>
            <View style={Styles.ColumnHeader} />
            <View style={Styles.ColumnBody}>
              <View>
                <View style={Styles.SubheaderView}>
                  <PdfImageFromAzureWithOptions
                    fileName="icon_location-dot.png"
                    folderPath="assets/icons/"
                    style={Styles.SubheaderIcon}
                  />
                  <Text style={Styles.Subheader}>
                    Community Characteristics
                  </Text>
                </View>
                <View style={{
                  display: 'flex',
                  flexDirection: 'row',
                }}>
                  <View style={{
                    width: '50%',
                  }}>
                    <DataBlurb
                      header="Walkability"
                      statistic={(reportData.eqi_land_walkability_value).toFixed(2)}
                      description={getWalkabilityLabel(reportData.eqi_land_walkability_value)} />
                    <DataBlurb
                      header="Uninsured Rate"
                      statistic={`${(reportData.ep_uninsur_value).toFixed(2)}%`} />
                  </View>
                  <View style={{
                    width: '50%',
                  }}>
                    <DataBlurb
                      header="Health Literacy"
                      statistic={`${(reportData.ep_limeng_value).toFixed(2)}%`}
                      description="Speak English 'Less than Well'" />
                    <DataBlurb
                      header="Poverty Rate"
                      statistic={`${(reportData.poverty_belowpoverty_value*100).toFixed(2)}%`} />
                  </View>
                </View>
              </View>
              <View>
                <View style={Styles.SubheaderView}>
                  <PdfImageFromAzureWithOptions
                    fileName="icon_chart-waterfall.png"
                    folderPath="assets/icons/"
                    style={Styles.SubheaderIcon}
                  />
                  <Text style={Styles.Subheader}>
                    Screening Rates
                  </Text>
                </View>
                <View style={{
                  display: 'flex',
                  flexDirection: 'row',
                }}>
                  <View style={{
                    width: '50%',
                  }}>
                    <DataBlurb
                      header="Breast Cancer"
                      statistic={(reportData.plcr_mammouse_value).toFixed(2)}
                      description="vs. 78.2% nat'l avg." />
                    <DataBlurb
                      header="Cervical Cancer"
                      statistic={(reportData.plcr_cervical_value).toFixed(2)}
                      description="vs. 78.0% nat'l avg." />
                  </View>
                  <View style={{
                    width: '50%',
                  }}>
                    <DataBlurb
                      header="Colorectal Cancer"
                      statistic={(reportData.plcr_colon_screen_value).toFixed(2)}
                      description="vs. 71.8% nat'l avg." />
                  </View>
                </View>
              </View>
              <View>
                <View style={Styles.SubheaderView}>
                  <PdfImageFromAzureWithOptions
                    fileName="icon_user-doctor-hair-long.png"
                    folderPath="assets/icons/"
                    style={Styles.SubheaderIcon}
                  />
                  <Text style={Styles.Subheader}>
                    Providers within {data.radius} {parseInt(data.radius) === 1 ? 'Mile' : 'Miles'}
                  </Text>
                </View>
                <DataGraph
                  label="Family Medicine"
                  value={NPIs.familyMedicine}
                  percent={(NPIs.familyMedicine/NPIsMax*100).toFixed(2)}
                />
                <DataGraph
                  label="General Practice"
                  value={NPIs.generalPractice}
                  percent={(NPIs.generalPractice/NPIsMax*100).toFixed(2)}
                />
                <DataGraph
                  label="Internal Medicine"
                  value={NPIs.internalMedicine}
                  percent={(NPIs.internalMedicine/NPIsMax*100).toFixed(2)}
                />
                <DataGraph
                  label="Pediatricians"
                  value={NPIs.pediatricians}
                  percent={(NPIs.pediatricians/NPIsMax*100).toFixed(2)}
                />
                <DataGraph
                  label="Psychologists"
                  value={NPIs.psychologist}
                  percent={(NPIs.psychologist/NPIsMax*100).toFixed(2)}
                />
              </View>
            </View>
            <View style={Styles.ColumnFooter} />
          </View>
          <View style={Styles.Column}>
            <View style={{ height: 96 }} /> 
            <View style={{ height: 444 }}>
              {formatTopThemes(data.topThemes, data.themeData, data.reportData.count)}
            </View>
          </View>
        </View>
        {
          formatForPrint
            && <PdfCropView
                  width={addBleed(PDF_WIDTH, formatForPrint, PDF_BLEED * 2)}
                  height={addBleed(PDF_HEIGHT, formatForPrint, PDF_BLEED * 2)}
                  markLength={PDF_BLEED}
                />
        }
        {
          formatForPrint
            &&
            <View
              style={{
                position: 'absolute',
                bottom: addBleed(18, formatForPrint),
                left: addBleed(36, formatForPrint),
                fontFamily: 'Courier',
                fontSize: 8,
                color: 'black',
                opacity: 0.35,
              }}
            >
              <Text>
                Generated for {organizationName} for a {data.radius}-mile radius.
              </Text>
            </View>
        }
      </Page>
      {
        printTemplate &&
          <ReportCardTemplatePageTwo formatForPrint={formatForPrint} />
      }
    </Document>
  );
};

export default ReportCard;

